import { useParams, Link } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useOrganization } from '../../../api/organizations';
import { UserRole } from '@spi/shared';
import { OrgIcon, ProjectIcon, UserIcon, DonorIcon, RecipientIcon } from '../shared/EntityIcons';
import { ProjectCard } from '../projects/ProjectCard';
import { PageMeta } from '../shared/PageMeta';

const markerIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml,' + encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 36" width="24" height="36">
      <path d="M12 0C5.4 0 0 5.4 0 12c0 9 12 24 12 24s12-15 12-24C24 5.4 18.6 0 12 0z" fill="#059669"/>
      <circle cx="12" cy="12" r="6" fill="white"/>
    </svg>
  `),
  iconSize: [24, 36],
  iconAnchor: [12, 36],
  popupAnchor: [0, -36],
});

export function OrganizationDetailPage() {
  const { id } = useParams();
  const orgId = parseInt(id || '0', 10);
  const { data: org, isLoading } = useOrganization(orgId);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-spi-green border-t-transparent" />
      </div>
    );
  }

  if (!org) {
    return <div className="container mx-auto px-4 py-16 text-center"><h2 className="text-2xl">Organization Not Found</h2></div>;
  }

  const RoleIcon = org.role === UserRole.DONOR ? DonorIcon : RecipientIcon;
  const roleBadgeColor = org.role === UserRole.DONOR ? 'bg-violet-100 text-violet-700' : 'bg-emerald-100 text-emerald-700';

  return (
    <div className="container mx-auto px-4 py-8">
      <PageMeta title={org.name} description={org.description || `${org.name} organization`} path={`/marketplace/organizations/${org.id}`} />

      <Link to="/marketplace/projects" className="text-sm text-spi-green hover:underline mb-4 inline-block">&larr; Back to projects</Link>

      {/* Header card */}
      <div className="card p-8 mb-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-2xl mb-1 flex items-center gap-2">
              <OrgIcon className="w-6 h-6 text-spi-green" />
              {org.name}
            </h2>
            <div className="flex flex-wrap items-center gap-2 mt-1">
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-spi-seed text-spi-earth capitalize">
                {org.type}
              </span>
              <span className="text-sm text-spi-slate">{org.country}</span>
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${roleBadgeColor} capitalize`}>
                <RoleIcon className="w-3 h-3" />
                {org.role}
              </span>
            </div>
          </div>
        </div>

        {org.description && (
          <p className="text-spi-charcoal mb-6 whitespace-pre-wrap">{org.description}</p>
        )}

        {/* Info grid */}
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 mb-6">
          {org.website && (
            <div>
              <span className="text-xs font-semibold text-spi-slate">Website</span>
              <p className="font-medium">
                <a href={org.website.startsWith('http') ? org.website : `https://${org.website}`} target="_blank" rel="noreferrer" className="text-spi-green hover:underline">
                  {org.website.replace(/^https?:\/\//, '')}
                </a>
              </p>
            </div>
          )}
          {org.address && (
            <div>
              <span className="text-xs font-semibold text-spi-slate">Address</span>
              <p className="font-medium">{org.address}</p>
            </div>
          )}
          <div>
            <span className="text-xs font-semibold text-spi-slate">Contact</span>
            <p className="font-medium">
              <UserIcon className="w-4 h-4 inline-block align-text-bottom text-spi-slate mr-1" />
              <Link to={`/marketplace/users/${org.contactUserId}`} className="text-spi-green hover:underline">
                {org.contactName}
              </Link>
            </p>
          </div>
        </div>

        {/* Mini map */}
        {org.latitude != null && org.longitude != null && (
          <div className="rounded-xl overflow-hidden border border-gray-200" style={{ height: '300px' }}>
            <MapContainer
              center={[org.latitude, org.longitude]}
              zoom={8}
              style={{ height: '100%', width: '100%' }}
              scrollWheelZoom={false}
              dragging={false}
              zoomControl={false}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
                url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}.png"
              />
              <Marker position={[org.latitude, org.longitude]} icon={markerIcon}>
                <Popup>{org.name}</Popup>
              </Marker>
            </MapContainer>
          </div>
        )}
      </div>

      {/* Active Projects */}
      <div className="mb-6">
        <h3 className="text-xl mb-4 flex items-center gap-2">
          <ProjectIcon className="w-5 h-5 text-spi-green" />
          Active Projects ({org.activeProjects.length})
        </h3>
        {org.activeProjects.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {org.activeProjects.map((project) => (
              <ProjectCard key={project.id} project={project} />
            ))}
          </div>
        ) : (
          <p className="text-spi-slate text-sm">No active projects at this time.</p>
        )}
      </div>
    </div>
  );
}
